<template>
  <div class="rp">
    <span class="qd-eyebrow">{{ $t('programs.Completion_reward') }}</span>

    <!-- Configured state: currency select + amount + reason + clear -->
    <div v-if="value" class="rp-body">
      <div class="rp-controls">
        <el-select
          :value="value.currency"
          @change="onCurrency"
          size="mini"
          class="rp-currency"
        >
          <el-option
            v-for="c in allowedCurrencies"
            :key="c"
            :value="c"
            :label="currencyLabel(c)"
          >
            <span class="rp-option">
              <i :class="currencyIcon(c)" />
              {{ currencyLabel(c) }}
            </span>
          </el-option>
        </el-select>

        <el-input-number
          :value="value.amount"
          @change="onAmount"
          :min="1"
          :max="500"
          :controls="false"
          size="mini"
          class="rp-amount"
        />

        <button
          type="button"
          class="rp-clear"
          @click="$emit('input', null)"
          aria-label="Remove reward"
        >
          <i class="el-icon-close" />
        </button>
      </div>

      <!-- Reason field — message sent to the recipient when they receive
           the reward, so they know why. Default seeded from the item title
           the first time the reward is added; freely editable after. -->
      <el-input
        :value="value.reason"
        @input="onReason"
        size="mini"
        class="rp-reason"
        :placeholder="$t('programs.Reward_reason_placeholder')"
      />
    </div>

    <!-- Empty state: a single low-noise add affordance -->
    <button v-else type="button" class="rp-add" @click="addDefault">
      <i class="el-icon-plus" />
      {{ $t('programs.Add_completion_reward') }}
    </button>
  </div>
</template>

<script>
const CURRENCY_ICONS = {
  coins: 'el-icon-coin',
  gems: 'el-icon-magic-stick',
  xp: 'el-icon-trophy',
}

const LABEL_KEY = {
  coins: 'programs.Currency_coins',
  gems: 'programs.Currency_gems',
  xp: 'programs.Currency_xp',
}

// Per-currency starting amount. Calibrated against the existing economy:
// coins are spendable so a modest 10 reads as a real reward without being
// extravagant; gems are scarcer and reflect "recognition moments" (mirrors
// the per-review grant pattern in performance.ts) so default to 1; XP is
// House-level and inflates quickly via Sessions, so 50 is meaningful but
// not outsized. Switching currency snaps the amount to the new default so
// authors don't accidentally grant 50 gems by leaving the previous value.
const DEFAULT_AMOUNTS = {
  coins: 10,
  gems: 1,
  xp: 50,
}

export default {
  name: 'reward-picker',
  props: {
    // CompletionReward | null. Two-way bound via v-model.
    value: { type: Object, default: null },
    // Surfaced to the picker so it can offer the right currency
    // options. All three currencies (coins / gems / xp) are available
    // on every type — keeps the authoring experience consistent.
    itemType: { type: String, required: true },
    // Default reason seeded when a reward is first added (e.g.
    // "For completing the 30-day check-in"). Computed by the parent
    // from the item title.
    defaultReason: { type: String, default: '' },
  },
  computed: {
    allowedCurrencies() {
      return ['coins', 'gems', 'xp']
    },
  },
  methods: {
    addDefault() {
      this.$emit('input', {
        currency: 'coins',
        amount: DEFAULT_AMOUNTS.coins,
        reason: this.defaultReason,
      })
    },
    onCurrency(currency) {
      // Snap to the new currency's default amount so authors don't
      // accidentally carry a 50-XP value over to a gem grant. Reason
      // is preserved — it's the author's content.
      this.$emit('input', {
        ...this.value,
        currency,
        amount: DEFAULT_AMOUNTS[currency] || 1,
      })
    },
    onAmount(amount) {
      this.$emit('input', { ...this.value, amount: Number(amount) || 0 })
    },
    onReason(reason) {
      this.$emit('input', { ...this.value, reason })
    },
    currencyIcon(c) {
      return CURRENCY_ICONS[c] || 'el-icon-coin'
    },
    currencyLabel(c) {
      return this.$t(LABEL_KEY[c] || 'programs.Currency_coins')
    },
  },
}
</script>

<style scoped>
.rp {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rp-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rp-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rp-currency { width: 110px; }
.rp-amount   { width: 96px; }
.rp-reason   { flex: 1; }

.rp-add {
  appearance: none;
  background: transparent;
  border: 1px dashed #e2e4ea;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}
.rp-add i { color: #c0c4cc; transition: color .15s ease; }
.rp-add:hover {
  border-color: rgba(108, 48, 204, 0.40);
  background: #f2ecfb;
  color: #6c30cc;
}
.rp-add:hover i { color: #6c30cc; }

.rp-clear {
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  color: #8a8a8a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background .15s ease, color .15s ease, border-color .15s ease;
}
.rp-clear:hover {
  background: rgba(204, 48, 48, 0.08);
  color: #cc3030;
  border-color: rgba(204, 48, 48, 0.20);
}

.rp-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.rp-option i { color: #c89500; font-size: 13px; }
</style>
